import React from 'react';
import './LegalPage.css';

const deletionSteps = [
  {
    title: 'Open the Budge App',
    description: 'Launch the Budge app on your iOS or Android device and make sure you are logged into the account you wish to delete.',
  },
  {
    title: 'Go to Profile',
    description: 'Tap the Profile tab in the bottom navigation bar (the person icon on the far right).',
  },
  {
    title: 'Export Your Data (Recommended)',
    description: 'Before proceeding, tap "Export CSV" on the Profile screen to download a copy of your transactions and financial data. This step is optional but recommended since deletion is permanent.',
  },
  {
    title: 'Tap "Delete Account"',
    description: 'Scroll down to the very bottom of the Profile screen and tap the "Delete Account" text button (shown in gray, below the Sign Out button).',
  },
  {
    title: 'First Confirmation — Tap "Delete Everything"',
    description: 'A dialog will appear saying "This will permanently delete your account and ALL your data (transactions, EMIs, goals, budgets). This action cannot be undone." Tap "Delete Everything" to proceed, or "Cancel" to go back.',
  },
  {
    title: 'Second Confirmation — Tap "Yes, Delete My Account"',
    description: 'A second dialog will ask "Are you absolutely sure? All data will be lost forever." Tap "Yes, Delete My Account" to confirm the permanent deletion.',
  },
  {
    title: 'Account Deleted',
    description: 'Your account and all data (transactions, EMIs, goals, budgets) will be permanently deleted from Firebase. You will be signed out and returned to the login screen.',
  },
];

function DeleteAccount() {
  return (
    <section className="legal-page">
      <div className="container">
        <div className="legal-content">
          <h1>Delete Your Account</h1>
          <span className="legal-date">Last updated: March 31, 2026</span>

          <p>
            We're sorry to see you go. If you'd like to delete your Budge account and all associated data,
            you can do so directly from within the app. Below are the step-by-step instructions.
          </p>

          <div className="warning-box">
            <p>
              <strong>Important:</strong> Before deleting your account, we recommend exporting your financial
              data as CSV from the Profile screen, as account deletion is permanent and irreversible.
            </p>
          </div>

          <h2>How to Delete Your Account</h2>

          <ol className="delete-steps">
            {deletionSteps.map((step, index) => (
              <li key={index}>
                <span className="step-num">{index + 1}</span>
                <div className="step-text">
                  <strong>{step.title}</strong>
                  <span>{step.description}</span>
                </div>
              </li>
            ))}
          </ol>

          <h2>What Gets Deleted</h2>
          <p>When you delete your account, the following data is permanently removed:</p>
          <ul>
            <li>Your profile information (name, email, photo)</li>
            <li>All transaction records (income and expenses)</li>
            <li>All EMI and loan information</li>
            <li>All budget allocations and history</li>
            <li>All savings goals and progress</li>
            <li>All uploaded receipt images</li>
            <li>Push notification preferences and tokens</li>
            <li>Your Firebase Authentication account</li>
          </ul>

          <div className="danger-box">
            <p>
              <strong>This action is permanent and cannot be undone.</strong> Once your account is deleted, we
              cannot recover any of your data. If you wish to use Budge again in the future, you will need to
              create a new account.
            </p>
          </div>

          <h2>Cancel Active Subscriptions</h2>
          <p>
            Deleting your Budge account does <strong>not</strong> automatically cancel your Pro subscription.
            If you have an active subscription, please cancel it separately:
          </p>
          <ul>
            <li><strong>iOS:</strong> Go to Settings &gt; Apple ID &gt; Subscriptions &gt; Budge &gt; Cancel Subscription</li>
            <li><strong>Android:</strong> Go to Google Play Store &gt; Menu &gt; Subscriptions &gt; Budge &gt; Cancel Subscription</li>
          </ul>

          <h2>Alternative: Contact Us</h2>
          <p>
            If you are unable to delete your account through the app (e.g., you cannot log in), you can request
            account deletion by emailing us at <a href="mailto:support@budgeapp.com">support@budgeapp.com</a> from
            the email address associated with your account. We will process your request within 7 business days.
          </p>

          <h2>Data Retention After Deletion</h2>
          <p>After you delete your account:</p>
          <ul>
            <li>All data is immediately removed from the active database.</li>
            <li>Backup copies may persist in our backup systems for up to 30 days before being automatically purged.</li>
            <li>We may retain anonymized, aggregated data that cannot be linked back to you for analytical purposes.</li>
          </ul>

          <p>
            If you have any questions about the deletion process, please contact us at{' '}
            <a href="mailto:support@budgeapp.com">support@budgeapp.com</a>.
          </p>
        </div>
      </div>
    </section>
  );
}

export default DeleteAccount;
